/**
 * Template Builder - Budget Step
 * 
 * Calculate and set estimated budget for the template
 */

import React, { useState, useMemo } from 'react';
import { View, StyleSheet, ScrollView, Pressable } from 'react-native';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import { InputField } from '@/components/ui/InputField';
import { SelectField } from '@/components/ui/SelectField';
import { Ionicons } from '@expo/vector-icons';
import type { TemplateBudget } from '@/types/tripTemplates';

type BudgetBreakdown = TemplateBudget['breakdown'];

const emptyBreakdown = (): BudgetBreakdown => ({
  flights: 0,
  lodging: 0,
  food: 0,
  activities: 0,
  transport: 0,
  other: 0,
});
import { CURRENCY_OPTIONS } from '@/data/profileOptions';

type TemplateBuilderBudgetStepProps = {
  draft: any;
  onUpdate: (updates: any) => void;
  errors: Record<string, string>;
};

const BUDGET_CATEGORIES: ReadonlyArray<{
  key: keyof BudgetBreakdown;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
}> = [
  { key: 'flights', label: '✈️ Flights', icon: 'airplane-outline' },
  { key: 'lodging', label: '🏨 Lodging', icon: 'bed-outline' },
  { key: 'food', label: '🍽️ Food & Dining', icon: 'restaurant-outline' },
  { key: 'activities', label: '🎯 Activities & Attractions', icon: 'ticket-outline' },
  { key: 'transport', label: '🚗 Local Transport', icon: 'car-outline' },
  { key: 'other', label: '📦 Other Expenses', icon: 'cube-outline' },
];

const parseCurrencyAmount = (text: string): number => {
  const digitsOnly = text.replace(/[^\d]/g, '');
  if (!digitsOnly) return 0;
  return Number.parseInt(digitsOnly, 10);
};

export const TemplateBuilderBudgetStep: React.FC<TemplateBuilderBudgetStepProps> = ({
  draft,
  onUpdate,
  errors,
}) => {
  const theme = useTheme();
  const budget = draft.estimatedBudget || {
    min: 0,
    max: 0,
    currency: 'USD',
    breakdown: emptyBreakdown(),
  };

  const [breakdown, setBreakdown] = useState<BudgetBreakdown>(() => ({
    ...emptyBreakdown(),
    ...(budget.breakdown ?? {}),
  }));

  // Calculate total from breakdown
  const totalFromBreakdown = useMemo(() => {
    return Object.values(breakdown).reduce((sum: number, val) => sum + (val ?? 0), 0);
  }, [breakdown]);

  // Update breakdown and recalculate min/max
  const handleBreakdownChange = (category: keyof BudgetBreakdown, value: number) => {
    const updated = { ...breakdown, [category]: value };
    setBreakdown(updated);
    
    // Auto-calculate min/max based on breakdown
    const total = Object.values(updated).reduce((sum: number, val) => sum + (val ?? 0), 0);
    const min = Math.round(total * 0.85); // 15% buffer for minimum
    const max = Math.round(total * 1.25); // 25% buffer for maximum
    
    onUpdate({
      estimatedBudget: {
        ...budget,
        breakdown: updated,
        min,
        max,
      },
    });
  };

  // Update min/max directly
  const handleMinMaxChange = (field: 'min' | 'max', value: number) => {
    const nextBudget = { ...budget, [field]: value };
    if (field === 'min' && value > (budget.max || 0)) {
      nextBudget.max = value;
    }
    if (field === 'max' && value < (budget.min || 0)) {
      nextBudget.min = value;
    }

    onUpdate({
      estimatedBudget: nextBudget,
    });
  };

  // Update currency
  const handleCurrencyChange = (currency: string) => {
    onUpdate({
      estimatedBudget: {
        ...budget,
        currency,
      },
    });
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <Card style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Budget Estimation</ThemedText>
        <Caption color={theme.colors.textSecondary} style={styles.sectionDescription}>
          Set estimated budget range and breakdown by category
        </Caption>

        {errors.budget && (
          <View style={[styles.errorContainer, { backgroundColor: theme.colors.error + '15' }]}>
            <Ionicons name="alert-circle" size={20} color={theme.colors.error} />
            <Caption color={theme.colors.error} style={styles.errorText}>
              {errors.budget}
            </Caption>
          </View>
        )}

        {/* Currency Selection */}
        <View style={styles.field}>
          <SelectField
            label="Currency"
            value={budget.currency || 'USD'}
            options={CURRENCY_OPTIONS}
            onValueChange={handleCurrencyChange}
          />
        </View>

        {/* Budget Range */}
        <View style={styles.budgetRange}>
          <View style={styles.rangeField}>
            <CurrencyAmountInput
              label="Minimum Budget"
              helperText="Lower estimate"
              value={budget.min || 0}
              currencySymbol={getCurrencySymbol(budget.currency || 'USD')}
              onValueChange={(value) => handleMinMaxChange('min', value)}
              quickAdds={[100, 500, 1000]}
            />
          </View>

          <View style={styles.rangeField}>
            <CurrencyAmountInput
              label="Maximum Budget"
              helperText="Upper estimate"
              value={budget.max || 0}
              minValue={budget.min || 0}
              currencySymbol={getCurrencySymbol(budget.currency || 'USD')}
              onValueChange={(value) => handleMinMaxChange('max', value)}
              quickAdds={[100, 500, 1000]}
            />
          </View>
        </View>

        {/* Budget Breakdown */}
        <View style={styles.breakdownSection}>
          <View style={styles.breakdownHeader}>
            <ThemedText style={styles.breakdownTitle}>Budget Breakdown</ThemedText>
            <Caption color={theme.colors.textSecondary}>
              Optional: Break down by category (auto-calculates total)
            </Caption>
          </View>

          <View style={styles.breakdownList}>
            {BUDGET_CATEGORIES.map((category) => {
              const value = breakdown[category.key] || 0;
              const percentage = totalFromBreakdown > 0
                ? Math.round((value / totalFromBreakdown) * 100)
                : 0;

              return (
                <Card key={category.key} style={styles.categoryCard}>
                  <View style={styles.categoryHeader}>
                    <View style={styles.categoryInfo}>
                      <Ionicons name={category.icon} size={20} color={theme.colors.primary} />
                      <View style={styles.categoryText}>
                        <Body style={styles.categoryLabel}>{category.label}</Body>
                        {totalFromBreakdown > 0 && (
                          <Caption color={theme.colors.textSecondary}>
                            {percentage}% of total
                          </Caption>
                        )}
                      </View>
                    </View>
                    <CurrencyAmountInput
                      value={value}
                      currencySymbol={getCurrencySymbol(budget.currency || 'USD')}
                      onValueChange={(val) => handleBreakdownChange(category.key, val)}
                      quickAdds={[50, 100, 500]}
                      compact
                    />
                  </View>
                </Card>
              );
            })}
          </View>

          {/* Total Summary */}
          {totalFromBreakdown > 0 && (
            <Card style={[styles.totalCard, { backgroundColor: theme.colors.primary + '10' }]}>
              <View style={styles.totalContent}>
                <ThemedText style={styles.totalLabel}>Estimated Total</ThemedText>
                <ThemedText style={[styles.totalAmount, { color: theme.colors.primary }]}>
                  {getCurrencySymbol(budget.currency || 'USD')}
                  {totalFromBreakdown.toLocaleString()}
                </ThemedText>
                <Caption color={theme.colors.textSecondary} style={styles.totalNote}>
                  Min: {getCurrencySymbol(budget.currency || 'USD')}
                  {budget.min?.toLocaleString() || 0} • Max: {getCurrencySymbol(budget.currency || 'USD')}
                  {budget.max?.toLocaleString() || 0}
                </Caption>
              </View>
            </Card>
          )}
        </View>

        {/* Quick Tips */}
        <View style={[styles.tipsCard, { backgroundColor: theme.colors.background }]}>
          <View style={styles.tipsHeader}>
            <Ionicons name="bulb-outline" size={20} color={theme.colors.primary} />
            <ThemedText style={styles.tipsTitle}>Budget Tips</ThemedText>
          </View>
          <View style={styles.tipsList}>
            <Caption color={theme.colors.textSecondary}>
              • Include flights, accommodation, meals, activities, and local transport
            </Caption>
            <Caption color={theme.colors.textSecondary}>
              • Set a realistic range - minimum for budget travelers, maximum for luxury
            </Caption>
            <Caption color={theme.colors.textSecondary}>
              • Breakdown is optional but helps users understand costs better
            </Caption>
            <Caption color={theme.colors.textSecondary}>
              • Costs will vary depending on where you're traveling from
            </Caption>
            <Caption color={theme.colors.textSecondary}>
              • You can adjust the template later by deleting, editing, or adding items
            </Caption>
          </View>
        </View>
      </Card>
    </ScrollView>
  );
};

type CurrencyAmountInputProps = {
  label?: string;
  helperText?: string;
  value: number;
  minValue?: number;
  currencySymbol: string;
  onValueChange: (value: number) => void;
  quickAdds?: number[];
  compact?: boolean;
};

const CurrencyAmountInput: React.FC<CurrencyAmountInputProps> = ({
  label,
  helperText,
  value,
  minValue = 0,
  currencySymbol,
  onValueChange,
  quickAdds = [],
  compact = false,
}) => {
  const theme = useTheme();
  const normalizedValue = Number.isFinite(value) ? Math.max(minValue, Math.round(value)) : minValue;
  const canDecrease = normalizedValue > minValue;

  const updateValue = (nextValue: number) => {
    onValueChange(Math.max(minValue, Math.min(1_000_000, Math.round(nextValue || 0))));
  };

  return (
    <View style={[styles.amountInputGroup, compact && styles.amountInputGroupCompact]}>
      {label && <ThemedText style={styles.fieldLabel}>{label}</ThemedText>}
      {helperText && (
        <Caption color={theme.colors.textSecondary} style={styles.fieldHelper}>
          {helperText}
        </Caption>
      )}
      <View style={styles.amountInputRow}>
        <ThemedText style={compact ? styles.currencySymbolSmall : styles.currencySymbol}>
          {currencySymbol}
        </ThemedText>
        <InputField
          value={normalizedValue > 0 ? String(normalizedValue) : ''}
          onChangeText={(text) => updateValue(parseCurrencyAmount(text))}
          placeholder="0"
          keyboardType="number-pad"
          inputMode="numeric"
          style={[styles.amountTextInput, compact && styles.amountTextInputCompact]}
          accessibilityLabel={label || 'Budget amount'}
        />
      </View>
      <View style={styles.quickAdjustRow}>
        <Pressable
          onPress={() => updateValue(normalizedValue - (compact ? 50 : 100))}
          disabled={!canDecrease}
          style={({ pressed }) => [
            styles.quickAdjustButton,
            {
              borderColor: theme.colors.borderMuted,
              opacity: canDecrease ? (pressed ? 0.7 : 1) : 0.35,
            },
          ]}
          accessibilityRole="button"
          accessibilityLabel="Decrease budget amount"
        >
          <Ionicons name="remove-outline" size={16} color={theme.colors.text} />
        </Pressable>
        {quickAdds.map((amount) => (
          <Pressable
            key={amount}
            onPress={() => updateValue(normalizedValue + amount)}
            style={({ pressed }) => [
              styles.quickAdjustButton,
              {
                borderColor: theme.colors.borderMuted,
                backgroundColor: pressed ? theme.colors.background : theme.colors.surface,
              },
            ]}
            accessibilityRole="button"
            accessibilityLabel={`Add ${currencySymbol}${amount}`}
          >
            <Caption style={styles.quickAdjustText}>+{amount.toLocaleString()}</Caption>
          </Pressable>
        ))}
      </View>
    </View>
  );
};

// Helper: Get currency symbol
const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    KRW: '₩',
    CNY: '¥',
    AUD: 'A$',
    CAD: 'C$',
    CHF: 'CHF',
    INR: '₹',
    SGD: 'S$',
    THB: '฿',
    MXN: 'MX$',
    BRL: 'R$',
  };
  return symbols[currency] || currency;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 16,
  },
  section: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  sectionDescription: {
    marginBottom: 16,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    flex: 1,
  },
  field: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  fieldHelper: {
    marginBottom: 8,
  },
  budgetRange: {
    gap: 16,
    marginBottom: 24,
  },
  rangeField: {
    gap: 8,
  },
  amountInputGroup: {
    gap: 8,
  },
  amountInputGroupCompact: {
    width: '100%',
  },
  amountInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  amountTextInput: {
    flex: 1,
    minWidth: 160,
    fontSize: 18,
    fontWeight: '600',
  },
  amountTextInputCompact: {
    minWidth: 112,
    fontSize: 16,
  },
  quickAdjustRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    paddingLeft: 38,
  },
  quickAdjustButton: {
    minHeight: 32,
    minWidth: 40,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickAdjustText: {
    fontSize: 12,
    fontWeight: '700',
  },
  currencyInput: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  currencySymbol: {
    fontSize: 18,
    fontWeight: '600',
    minWidth: 30,
  },
  currencySymbolSmall: {
    fontSize: 14,
    fontWeight: '600',
    minWidth: 20,
  },
  breakdownSection: {
    marginTop: 8,
  },
  breakdownHeader: {
    marginBottom: 16,
  },
  breakdownTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  breakdownList: {
    gap: 12,
    marginBottom: 16,
  },
  categoryCard: {
    padding: 12,
  },
  categoryHeader: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
  },
  categoryInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0,
  },
  categoryText: {
    flex: 1,
  },
  categoryLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  categoryInput: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minWidth: 148,
    flexShrink: 0,
  },
  totalCard: {
    padding: 16,
    marginTop: 8,
  },
  totalContent: {
    alignItems: 'center',
    gap: 4,
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  totalAmount: {
    fontSize: 24,
    fontWeight: '700',
  },
  totalNote: {
    marginTop: 4,
    fontSize: 12,
  },
  tipsCard: {
    padding: 16,
    borderRadius: 8,
    marginTop: 16,
  },
  tipsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  tipsTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  tipsList: {
    gap: 6,
  },
});
